import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Keyboard as KeyboardIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';

interface KeyboardShortcut {
  action: string;
  shortcut: string;
  description: string;
  category: 'navigation' | 'file' | 'view' | 'selection';
}

const KEYBOARD_SHORTCUTS: KeyboardShortcut[] = [
  // Navigation shortcuts
  {
    action: 'navigate_home',
    shortcut: 'Alt + H',
    description: 'Navigate to home',
    category: 'navigation',
  },
  {
    action: 'navigate_favorites',
    shortcut: 'Alt + F',
    description: 'Navigate to favorites',
    category: 'navigation',
  },
  {
    action: 'navigate_recent',
    shortcut: 'Alt + R',
    description: 'Navigate to recent files',
    category: 'navigation',
  },
  {
    action: 'navigate_trash',
    shortcut: 'Alt + T',
    description: 'Navigate to trash',
    category: 'navigation',
  },
  
  // File operations
  {
    action: 'copy',
    shortcut: 'Ctrl + C',
    description: 'Copy selected files/folders',
    category: 'file',
  },
  {
    action: 'cut',
    shortcut: 'Ctrl + X',
    description: 'Cut selected files/folders',
    category: 'file',
  },
  {
    action: 'paste',
    shortcut: 'Ctrl + V',
    description: 'Paste files/folders',
    category: 'file',
  },
  {
    action: 'delete',
    shortcut: 'Delete',
    description: 'Delete selected files/folders',
    category: 'file',
  },
  {
    action: 'rename',
    shortcut: 'F2',
    description: 'Rename selected file/folder',
    category: 'file',
  },
  {
    action: 'new_folder',
    shortcut: 'Shift + N',
    description: 'Create new folder',
    category: 'file',
  },
  {
    action: 'upload',
    shortcut: 'Shift + U',
    description: 'Upload file',
    category: 'file',
  },
  
  // View shortcuts
  {
    action: 'refresh',
    shortcut: 'F5',
    description: 'Refresh current view',
    category: 'view',
  },
  {
    action: 'search',
    shortcut: 'Ctrl + F',
    description: 'Search files',
    category: 'view',
  },
  {
    action: 'toggle_view',
    shortcut: 'Alt + V',
    description: 'Toggle between grid and list view',
    category: 'view',
  },
  {
    action: 'help',
    shortcut: 'F1',
    description: 'Show keyboard shortcuts help',
    category: 'view',
  },
  
  // Selection shortcuts
  {
    action: 'select_all',
    shortcut: 'Ctrl + A',
    description: 'Select all items in current folder',
    category: 'selection',
  },
];

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
}

const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({ open, onClose }) => {
  const theme = useTheme();
  
  const renderShortcutsByCategory = (category: KeyboardShortcut['category']) => {
    const shortcuts = KEYBOARD_SHORTCUTS.filter(
      (shortcut) => shortcut.category === category
    );
    
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
          {category.charAt(0).toUpperCase() + category.slice(1)}
        </Typography>
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <List dense disablePadding>
            {shortcuts.map((shortcut, index) => (
              <React.Fragment key={shortcut.action}>
                <ListItem sx={{ py: 1.5 }}>
                  <ListItemText 
                    primary={shortcut.description} 
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {shortcut.shortcut}
                    </Typography>
                  </Box>
                </ListItem>
                {index < shortcuts.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Box>
    );
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <KeyboardIcon color="primary" />
        <Typography variant="h6">Keyboard Shortcuts</Typography>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="textSecondary" paragraph>
          Use these keyboard shortcuts to navigate and perform actions quickly without using the mouse.
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {renderShortcutsByCategory('navigation')}
            {renderShortcutsByCategory('view')}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderShortcutsByCategory('file')}
            {renderShortcutsByCategory('selection')}
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default KeyboardShortcutsHelp; 